import React, { useEffect, useRef, useState } from 'react';

const decodeHTML = (html) => {
  if (!html || typeof html !== 'string') return html;
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

// Global state to track MathJax script loading
let isScriptLoaded = !!window.MathJax?.typesetPromise;
let isScriptLoading = false;
let loadCallbacks = [];

const loadMathJax = () => {
  if (isScriptLoaded) return Promise.resolve();
  if (isScriptLoading) {
    return new Promise((resolve) => {
      loadCallbacks.push(resolve);
    });
  }

  isScriptLoading = true;
  return new Promise((resolve, reject) => {
    window.MathJax = {
      ...(window.MathJax || {}),
      loader: {
        ...(window.MathJax?.loader || {}),
        load: Array.from(new Set([...(window.MathJax?.loader?.load || []), '[tex]/html', '[tex]/enclose']))
      },
      tex: {
        ...(window.MathJax?.tex || {}),
        inlineMath: window.MathJax?.tex?.inlineMath || [['\\(', '\\)'], ['$', '$']],
        displayMath: window.MathJax?.tex?.displayMath || [['\\[', '\\]'], ['$$', '$$']],
        packages: {
          ...(window.MathJax?.tex?.packages || {}),
          '[+]': Array.from(new Set([...(window.MathJax?.tex?.packages?.['[+]'] || []), 'html', 'enclose']))
        }
      }
    };

    const script = document.createElement('script');
    script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
    script.id = "MathJax-script";
    script.async = true;
    script.onload = () => {
      isScriptLoaded = true;
      isScriptLoading = false;
      resolve();
      loadCallbacks.forEach(cb => cb());
      loadCallbacks = [];
    };
    script.onerror = () => {
      isScriptLoading = false;
      reject(new Error('MathJax failed to load'));
    };
    document.head.appendChild(script);
  });
};

const hasMath = (str) => {
  if (!str || typeof str !== 'string') return false;
  return str.includes('$') || 
         str.includes('\\(') || 
         str.includes('\\[') || 
         str.includes('<svg') || 
         str.includes('<math') || 
         str.includes('[SVG-');
};

const MathText = ({ text = '', className = '', style = {}, targetId = '' }) => {
  const ref = useRef(null);
  const needsMathJax = hasMath(text);
  const [isHydrated, setIsHydrated] = useState(!needsMathJax);
  const [isVisible, setIsVisible] = useState(!needsMathJax);

  const handleSvgClick = (e) => {
    const svgNode = e.target.closest('svg');
    if (svgNode && targetId) {
      e.stopPropagation();
      e.preventDefault();
      
      // Dispatch custom edit event carried with target input identity
      const event = new CustomEvent('edit-svg-diagram', {
        detail: {
          targetId: targetId,
          svgHtml: svgNode.outerHTML
        }
      });
      window.dispatchEvent(event);
    }
  };

  useEffect(() => {
    setIsHydrated(!needsMathJax);
    setIsVisible(!needsMathJax);
  }, [needsMathJax]);

  useEffect(() => {
    if (!ref.current || !needsMathJax) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(ref.current);
        }
      },
      { rootMargin: '200px' } // Load math 200px before it enters viewport
    );

    observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [needsMathJax]);

  useEffect(() => {
    if (!ref.current || !text || !isVisible || !needsMathJax) return;

    const typeset = async () => {
      try {
        if (!window.MathJax?.typesetPromise) {
          await loadMathJax();
        }
        
        if (window.MathJax?.typesetPromise && ref.current) {
          window.MathJax.typesetPromise([ref.current]).then(() => {
            setIsHydrated(true);
          }).catch(err => {
            console.error('MathJax typeset error:', err);
          });
        }
      } catch (err) {
        console.error('Dynamic MathJax load error:', err);
      }
    };

    typeset();
    // Retry shortly after mount/update to catch slow MathJax loads
    const timer = setTimeout(typeset, 500);
    return () => clearTimeout(timer);
  }, [text, isVisible, needsMathJax]);

  const combinedClass = `math-text ${className} ${isHydrated ? 'hydrated' : ''}`.trim();
  const decodedText = decodeHTML(text);
  
  // Expand placeholders [SVG-X] back to full SVGs for rendering and interactivity
  let expandedText = decodedText;
  if (window.svgDiagrams) {
    for (const [key, val] of Object.entries(window.svgDiagrams)) {
      expandedText = expandedText.replace(new RegExp(key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g'), val);
    }
  }

  // Decide if we should be block or inline based on content
  const isComplex = expandedText && (expandedText.includes('<svg') || expandedText.includes('<math') || expandedText.includes('$$') || expandedText.includes('\\['));

  return (
    <div
      ref={ref}
      className={combinedClass}
      onClick={handleSvgClick}
      style={{ 
        display: isComplex ? 'block' : 'inline-block', 
        verticalAlign: 'middle',
        maxWidth: '100%',
        wordBreak: 'break-word',
        opacity: isHydrated || !isComplex ? 1 : 0.6, // Dim complex math until hydrated
        transition: 'opacity 0.3s ease',
        ...style 
      }}
      dangerouslySetInnerHTML={{ __html: String(expandedText) }}
    />
  );
};

export default MathText;
